import { ClipboardList, Inbox, Plus, SpellCheck } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next'; // <-- NEU
import PageNotFoundImage from '../../../../assets/page-not-found.svg';
import { cn } from '../../../../lib/utils.js';
import { useVerticalSplit } from '../../use-vertical-split.js';
import { VerticalResizeHandle } from '../../vertical-resize-handle.js';
import {
  AiGradingPanel,
  ExtractedTextPanel,
  GradingSchemePanel,
  MiddleBar,
  TopBar,
} from './components/grading-panels.js';
import { GrammarOverlay, GrammarPanel } from './components/grammar-panels.js';
import { InteractiveBox } from './components/interactive-box.js';
import { useGradingState } from './use-grading-state.js';
import type { CombinedCorrectionProps } from './utils.js';

const DEFAULT_LEFT_PANEL_WIDTH = 60; // percentage
const RESIZE_STORAGE_KEY = 'gradias-canvas-left-panel-width';

export function CombinedTaskCorrection(props: CombinedCorrectionProps) {
  const { t } = useTranslation();
  const store = useGradingState(props);
  const { state, computed, actions, refs } = store;

  const isGrammarMode = computed.mode === 'grammar';
  const [selectedGrammarErrorId, setSelectedGrammarErrorId] = useState<string | null>(null);

  const mode = computed.mode;
  // Grammar evaluations can live on a nested subtask (e.g. when a split essay/poetry task is
  // combined into one main task with subtasks) rather than the top-level task itself, so this must
  // walk the whole subTasksSubmission tree, not just the flat top level.
  const hasGrammarEvalRecursive = (task: any): boolean =>
    Boolean(task?.grammarEvaluation) || (task?.subTasksSubmission ?? []).some(hasGrammarEvalRecursive);
  const hasAnyGrammarEval = computed.submissions.some(s => (s.taskSubmissions ?? []).some(hasGrammarEvalRecursive));
  useEffect(() => {
    if (!props.onHeaderContent) return;
    if (!hasAnyGrammarEval || mode === 'answers') {
      props.onHeaderContent(null);
      return;
    }
    props.onHeaderContent(
      <div className="flex items-center gap-2 min-w-0">
        <button
          onClick={() => actions.setMode('correction')}
          className={cn(
            'flex items-center gap-1.5 min-w-0 transition-colors',
            mode === 'correction'
              ? 'font-semibold text-slate-700 dark:text-slate-300 cursor-default'
              : 'font-medium text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 hover:underline',
          )}
        >
          <ClipboardList size={16} className="shrink-0" />
          <span className="hidden overflow-hidden text-ellipsis whitespace-nowrap @[8rem]:inline @[8rem]:min-w-[24px]">
            {t('Canvas.grading.modeCorrection')}
          </span>
        </button>
        <span className="text-slate-300 dark:text-slate-600 select-none shrink-0">|</span>
        <button
          onClick={() => actions.setMode('grammar')}
          className={cn(
            'flex items-center gap-1.5 min-w-0 transition-colors',
            mode === 'grammar'
              ? 'font-semibold text-red-600 dark:text-red-400 cursor-default'
              : 'font-medium text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 hover:underline',
          )}
        >
          <SpellCheck size={16} className="shrink-0" />
          <span className="hidden overflow-hidden text-ellipsis whitespace-nowrap @[8rem]:inline @[8rem]:min-w-[24px]">
            {t('Canvas.grading.modeGrammar')}
          </span>
        </button>
      </div>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, hasAnyGrammarEval]);

  useEffect(() => {
    if (!props.onVisibleTaskChange) return;
    const taskNumber = computed.currentSubTask?.taskNumber ?? computed.currentMainTask?.taskNumber ?? null;
    props.onVisibleTaskChange({
      taskNumber,
      studentId: computed.currentSubmission?._id ?? null,
      studentName: computed.currentSubmission?.studentName ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed.currentSubTask, computed.currentMainTask, computed.currentSubmission]);

  const handleSelectGrammarError = (id: string) => {
    setSelectedGrammarErrorId(id);
  };

  const handleDeleteGrammarError = (errorId: string) => {
    const submissionId = computed.currentSubmission?._id;
    const mainTaskId = computed.currentMainTask?._id;
    if (!submissionId || !mainTaskId || !props.onDeleteGrammarError) return;
    void props.onDeleteGrammarError(submissionId, mainTaskId, errorId);
    if (selectedGrammarErrorId === errorId) setSelectedGrammarErrorId(null);
    if (editingErrorId === errorId) {
      setEditingErrorId(null);
      setEditingBox(null);
    }
  };

  // ---- Manual grammar error editing state ----
  const [editingErrorId, setEditingErrorId] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState('TYPOS');
  const [editingMessage, setEditingMessage] = useState('');
  const [editingBox, setEditingBox] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
    pageNumber: number;
  } | null>(null);

  // Ref holds the latest values so the debounce timer reads current state, not a stale closure.
  const editingLatestRef = useRef({ category: 'TYPOS', message: '', box: null as typeof editingBox });
  editingLatestRef.current = { category: editingCategory, message: editingMessage, box: editingBox };

  const updateDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pageContainerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const grammarScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!selectedGrammarErrorId || !isGrammarMode) return;
    const error = computed.currentGrammarEvaluation?.errors.find(e => e.id === selectedGrammarErrorId);
    if (!error?.box) return;
    const page = pageContainerRefs.current[error.box.pageNumber];
    const scrollContainer = grammarScrollRef.current;
    if (!page || !scrollContainer) return;
    const errorAbsoluteY = page.offsetTop + page.offsetHeight * (error.box.y / 100);
    const scrollTarget = errorAbsoluteY - scrollContainer.clientHeight / 3;
    scrollContainer.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' });
  }, [selectedGrammarErrorId]);

  const flushPendingUpdate = () => {
    if (!updateDebounceRef.current || !editingErrorId) return;
    clearTimeout(updateDebounceRef.current);
    updateDebounceRef.current = null;
    const { category, message, box } = editingLatestRef.current;
    const submissionId = computed.currentSubmission?._id;
    const mainTaskId = computed.currentMainTask?._id;
    if (submissionId && mainTaskId && box) {
      void props.onUpdateGrammarError?.(submissionId, mainTaskId, editingErrorId, category, message, box);
    }
  };

  const scheduleUpdate = () => {
    if (updateDebounceRef.current) clearTimeout(updateDebounceRef.current);
    updateDebounceRef.current = setTimeout(() => {
      const { category, message, box } = editingLatestRef.current;
      const submissionId = computed.currentSubmission?._id;
      const mainTaskId = computed.currentMainTask?._id;
      if (submissionId && mainTaskId && editingErrorId && box) {
        void props.onUpdateGrammarError?.(submissionId, mainTaskId, editingErrorId, category, message, box);
      }
    }, 800);
  };

  // Flush + stop editing when the user selects a different error.
  useEffect(() => {
    if (editingErrorId && selectedGrammarErrorId !== editingErrorId) {
      flushPendingUpdate();
      setEditingErrorId(null);
      setEditingBox(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGrammarErrorId]);

  const handleSaveEditing = () => {
    flushPendingUpdate();
    setEditingErrorId(null);
    setEditingBox(null);
  };

  const handleStartEditing = (errorId: string) => {
    const error = computed.currentGrammarEvaluation?.errors.find(e => e.id === errorId);
    if (!error) return;
    flushPendingUpdate();
    setEditingErrorId(errorId);
    setEditingCategory(error.category || 'TYPOS');
    setEditingMessage(error.message || '');
    setEditingBox(error.box ? { ...error.box } : null);
    setSelectedGrammarErrorId(errorId);
  };

  const handleAddMistake = async (pageIdx: number) => {
    const submissionId = computed.currentSubmission?._id;
    const mainTaskId = computed.currentMainTask?._id;
    if (!submissionId || !mainTaskId || !props.onAddGrammarError) return;
    const newId = crypto.randomUUID();
    const box = { x: 5, y: 5, width: 40, height: 6, pageNumber: pageIdx };
    const ok = await props.onAddGrammarError(submissionId, mainTaskId, {
      _id: newId,
      category: 'TYPOS',
      message: '',
      box,
    });
    if (!ok) return;
    setEditingErrorId(newId);
    setEditingCategory('TYPOS');
    setEditingMessage('');
    setEditingBox(box);
    setSelectedGrammarErrorId(newId);
  };

  const handleEditingCategoryChange = (cat: string) => {
    setEditingCategory(cat);
    scheduleUpdate();
  };

  const handleEditingMessageChange = (msg: string) => {
    setEditingMessage(msg);
    scheduleUpdate();
  };

  const handleEditingBoxChange = (box: { x: number; y: number; width: number; height: number }) => {
    if (!editingBox) return;
    setEditingBox({ ...box, pageNumber: editingBox.pageNumber });
  };

  const handleEditingBoxChangeEnd = (box: { x: number; y: number; width: number; height: number }) => {
    if (!editingBox) return;
    const newBox = { ...box, pageNumber: editingBox.pageNumber };
    setEditingBox(newBox);
    scheduleUpdate();
  };

  // The right-hand detail panels: grading panels in answer/correction mode, grammar cards in grammar mode.
  // The three grading panels are individually resizable (nested vertical splits) regardless of canvas
  // width — the narrow layout gives this container a definite height (see the narrowSplit-based wrapper
  // below) so the percentage splits here have something to size against.
  const renderDetailPanels = () => {
    if (isGrammarMode) {
      return (
        <GrammarPanel
          evaluation={computed.currentGrammarEvaluation}
          selectedErrorId={selectedGrammarErrorId}
          onSelect={handleSelectGrammarError}
          onDeleteError={props.onDeleteGrammarError ? handleDeleteGrammarError : undefined}
          onEditError={handleStartEditing}
          onSaveError={handleSaveEditing}
          editingErrorId={editingErrorId}
          editingCategory={editingCategory}
          editingMessage={editingMessage}
          onEditingCategoryChange={handleEditingCategoryChange}
          onEditingMessageChange={handleEditingMessageChange}
        />
      );
    }

    // Answer-review mode only ever shows the extracted text — GradingSchemePanel and AiGradingPanel
    // render null in this mode — so it gets the full height instead of two resize handles over empty panels.
    if (mode === 'answers') {
      return (
        <div className="flex flex-1 flex-col min-h-0">
          <ExtractedTextPanel store={store} />
        </div>
      );
    }

    // ExtractedTextPanel and AiGradingPanel render null under some conditions (no box selected /
    // excerpt-only task) — mirror their own early-return checks here so an invisible panel doesn't
    // still reserve a split's worth of empty vertical space next to the panels that do render.
    const showExtractedText = Boolean(state.selectedBoxId) && !computed.showExcerptOnlyForMainTask;
    const showAiGrading = !computed.showExcerptOnlyForMainTask;
    // GradingSchemePanel collapses itself to a shrink-0 header (defaults to collapsed) instead of
    // filling flex-1 — giving it a fixed split percentage regardless would leave a gap below the
    // collapsed header, so only split against it while it's expanded.
    const isSchemeExpanded = !state.isGradingSchemeCollapsed;
    const showSchemeAiSplit = showAiGrading && isSchemeExpanded;

    return (
      <div ref={detailSplitTop.containerRef} className="relative flex flex-1 flex-col min-h-0">
        {showExtractedText && (
          <>
            <div className="flex flex-col min-h-0" style={{ height: `calc(${detailSplitTop.topPercent}% - 8px)` }}>
              <ExtractedTextPanel store={store} />
            </div>

            <VerticalResizeHandle
              topPercent={detailSplitTop.topPercent}
              isResizing={detailSplitTop.isResizing}
              onPointerDown={detailSplitTop.startResizing}
            />
          </>
        )}

        <div
          ref={detailSplitBottom.containerRef}
          className="relative flex flex-col gap-4 min-h-0"
          style={{ height: showExtractedText ? `calc(${100 - detailSplitTop.topPercent}% - 8px)` : '100%' }}
        >
          <div
            className={cn('flex flex-col', isSchemeExpanded ? 'min-h-0' : 'shrink-0')}
            style={
              isSchemeExpanded
                ? { height: showAiGrading ? `calc(${detailSplitBottom.topPercent}% - 8px)` : '100%' }
                : undefined
            }
          >
            <GradingSchemePanel store={store} />
          </div>

          {showSchemeAiSplit && (
            <VerticalResizeHandle
              topPercent={detailSplitBottom.topPercent}
              isResizing={detailSplitBottom.isResizing}
              onPointerDown={detailSplitBottom.startResizing}
            />
          )}

          {showAiGrading && (
            <div
              className="flex flex-col min-h-0"
              style={
                isSchemeExpanded ? { height: `calc(${100 - detailSplitBottom.topPercent}% - 8px)` } : { flex: 1 }
              }
            >
              <AiGradingPanel store={store} />
            </div>
          )}
        </div>
      </div>
    );
  };

  // State for resizable panels
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LEFT_PANEL_WIDTH;
    const stored = localStorage.getItem(RESIZE_STORAGE_KEY);
    return stored ? Math.max(30, Math.min(70, parseInt(stored, 10))) : DEFAULT_LEFT_PANEL_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(0);
  const containerWidthRef = useRef(0);

  // Vertical split between the image viewer and the details panels — only relevant on narrow canvases,
  // where they stack instead of sitting side by side.
  const narrowSplit = useVerticalSplit('gradias-grading-narrow-vertical-split', 55, 20, 80);

  // Vertical splits between the individual detail boxes (extracted text / grading scheme / AI grading) —
  // desktop layout only, since on narrow canvases those panels already use fixed/collapsible heights of
  // their own that don't fit a percentage split.
  const detailSplitTop = useVerticalSplit('gradias-grading-detail-split-top', 40, 15, 70);
  const detailSplitBottom = useVerticalSplit('gradias-grading-detail-split-bottom', 50, 15, 85);

  // Handle resize start
  const handleResizeStart = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const container = refs.layoutContainerRef.current;
    if (!container) return;

    setIsResizing(true);
    resizeStartXRef.current = e.clientX;
    resizeStartWidthRef.current = leftPanelWidth;
    containerWidthRef.current = container.offsetWidth;

    // Add class to body to prevent text selection and show cursor everywhere
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Handle resize move
  useEffect(() => {
    if (!isResizing) return;

    const handlePointerMove = (e: PointerEvent) => {
      const deltaX = e.clientX - resizeStartXRef.current;
      const deltaPercent = (deltaX / containerWidthRef.current) * 100;
      const newWidth = Math.max(30, Math.min(70, resizeStartWidthRef.current + deltaPercent));
      setLeftPanelWidth(newWidth);
    };

    const handlePointerUp = () => {
      setIsResizing(false);
      localStorage.setItem(RESIZE_STORAGE_KEY, Math.round(leftPanelWidth).toString());

      // Cleanup body styles
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isResizing, leftPanelWidth]);

  if (computed.submissions.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
          <Inbox className="h-7 w-7 text-slate-400 dark:text-slate-500" />
        </div>
        <p className="font-medium text-slate-500 dark:text-slate-400">{t('Canvas.grading.noSubmissionsFound')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-4 relative">
      {/* 1. Header & Navigation */}
      <TopBar store={store} />

      {/* 2. Pagination / Page Indicators — hidden in grammar mode (all pages scroll continuously) */}
      {!isGrammarMode && <MiddleBar store={store} />}

      {/* 3. Main Content Layout */}
      {state.isCanvasNarrow ? (
        // Mobile/Narrow layout
        <div
          ref={el => {
            refs.layoutContainerRef.current = el;
            narrowSplit.containerRef.current = el;
          }}
          className={cn('flex flex-col flex-1 gap-4 min-h-0 relative', narrowSplit.isResizing && 'select-none')}
        >
          {/* Image Viewer & Bounding Boxes */}
          <div
            ref={isGrammarMode ? grammarScrollRef : undefined}
            className={cn(
              'overflow-auto bg-slate-200/50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 p-2 custom-scrollbar min-h-0',
              isGrammarMode ? 'flex flex-col gap-4 items-center' : 'flex justify-center items-start',
            )}
            style={{ height: `calc(${narrowSplit.topPercent}% - 8px)` }}
          >
            {isGrammarMode ? (
              computed.pages.map((pageUrl, pageIdx) => (
                <div
                  key={pageIdx}
                  ref={el => {
                    pageContainerRefs.current[pageIdx] = el;
                  }}
                  className="relative w-full shadow-xl bg-white dark:bg-slate-800"
                >
                  <img
                    src={pageUrl}
                    alt={t('Canvas.grading.examPageX', { page: pageIdx + 1 })}
                    className="w-full h-auto block pointer-events-none select-none"
                    onError={e => {
                      e.currentTarget.src = PageNotFoundImage;
                    }}
                  />
                  <GrammarOverlay
                    evaluation={computed.currentGrammarEvaluation}
                    pageIndex={pageIdx}
                    selectedErrorId={selectedGrammarErrorId}
                    onSelect={handleSelectGrammarError}
                    editingBox={editingBox}
                    containerRef={{ current: pageContainerRefs.current[pageIdx] ?? null }}
                    onEditingBoxChange={handleEditingBoxChange}
                    onEditingBoxChangeEnd={handleEditingBoxChangeEnd}
                  />
                  {props.onAddGrammarError && (
                    <button
                      className="absolute top-2 right-2 z-30 flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-white/90 dark:bg-slate-700/90 border border-slate-300 dark:border-slate-600 shadow text-slate-700 dark:text-slate-200 hover:bg-red-50 dark:hover:bg-red-900/30 hover:border-red-400 transition-colors"
                      onClick={e => {
                        e.stopPropagation();
                        void handleAddMistake(pageIdx);
                      }}
                    >
                      <Plus size={12} className="mr-0.5" />
                      {t('Canvas.grading.addMistake')}
                    </button>
                  )}
                </div>
              ))
            ) : (<div
              ref={refs.imageContainerRef}
              className="relative shadow-xl transition-transform duration-200 ease-out origin-top bg-white dark:bg-slate-800"
              style={{ transform: `scale(${state.zoom})`, width: '800px' }}
            >
              <img
                src={computed.pages[state.currentPage]}
                alt={t('Canvas.grading.examPageX', { page: state.currentPage + 1 })}
                className="w-full h-auto block pointer-events-none select-none"
                onError={e => {
                  e.currentTarget.src = PageNotFoundImage;
                }}
              />

              {state.localTaskBoxes
                .filter(box => box.pageNumber === state.currentPage)
                .map(box => {
                  const boxIndex = state.localTaskBoxes.findIndex(existingBox => existingBox.id === box.id);
                  const indexedBoxId = `box-${boxIndex}`;

                  return (
                    <InteractiveBox
                      key={box.id}
                      box={box}
                      isSelected={state.selectedBoxId === box.id}
                      isApproved={computed.mode !== 'answers' && state.localIsApproved}
                      containerRef={refs.imageContainerRef}
                      onSelect={() => actions.setSelectedBoxId(box.id)}
                      onChange={newBox => {
                        actions.setLocalTaskBoxes(prev =>
                          prev.map(existingBox => (existingBox.id === box.id ? newBox : existingBox)),
                        );
                      }}
                      onChangeEnd={async newBox => {
                        if (boxIndex < 0) return;
                        await actions.saveUpdate(indexedBoxId, { boxCoordinates: newBox });
                      }}
                      onDelete={async () => {
                        if (boxIndex < 0) return;
                        actions.setLocalTaskBoxes(prev => prev.filter(existingBox => existingBox.id !== box.id));
                        if (state.selectedBoxId === box.id) {
                          actions.setSelectedBoxId(null);
                          actions.setIsEditingText(false);
                        }
                        await actions.saveUpdate(indexedBoxId, { deleteBoundingBox: true });
                      }}
                      onRetry={async () => {
                        if (boxIndex < 0) return;
                        await actions.saveUpdate(indexedBoxId, {
                          boxCoordinates: {
                            x: box.x,
                            y: box.y,
                            width: box.width,
                            height: box.height,
                            pageNumber: box.pageNumber,
                          },
                          rerunOcr: true,
                        });
                      }}
                      onToggleLock={async () => {
                        if (boxIndex < 0) return;
                        const nextApproved = box.approved !== true;
                        actions.setLocalTaskBoxes(prev =>
                          prev.map(existingBox =>
                            existingBox.id === box.id ? { ...existingBox, approved: nextApproved } : existingBox,
                          ),
                        );
                        await actions.saveUpdate(indexedBoxId, { boxApproved: nextApproved });
                      }}
                    />
                  );
                })}
            </div>)}
          </div>

          <VerticalResizeHandle
            topPercent={narrowSplit.topPercent}
            isResizing={narrowSplit.isResizing}
            onPointerDown={narrowSplit.startResizing}
          />

          {/* Details Panels */}
          <div
            className="flex flex-col gap-4 min-h-0 overflow-auto"
            style={{ height: `calc(${100 - narrowSplit.topPercent}% - 8px)` }}
          >
            {renderDetailPanels()}
          </div>
        </div>
      ) : (
        <div
          ref={refs.layoutContainerRef}
          className={cn(
            'flex flex-1 gap-0 min-h-0 bg-slate-200/50 dark:bg-slate-800/50 rounded-xl relative border border-slate-200 dark:border-slate-700 shadow-inner',
            isResizing && 'select-none',
          )}
        >
          {/* Left Panel - Image Viewer */}
          <div
            ref={isGrammarMode ? grammarScrollRef : undefined}
            className={cn(
              'overflow-auto p-2 custom-scrollbar h-full bg-white dark:bg-card rounded-l-xl',
              isGrammarMode ? 'flex flex-col gap-4 items-center' : 'flex justify-center items-start',
            )}
            style={{ width: `${leftPanelWidth}%` }}
          >
            {isGrammarMode ? (
              computed.pages.map((pageUrl, pageIdx) => (
                <div
                  key={pageIdx}
                  ref={el => {
                    pageContainerRefs.current[pageIdx] = el;
                  }}
                  className="relative w-full shadow-xl bg-white dark:bg-slate-800 rounded-xl"
                >
                  <img
                    src={pageUrl}
                    alt={t('Canvas.grading.examPageX', { page: pageIdx + 1 })}
                    className="w-full h-auto block pointer-events-none select-none"
                    onError={e => {
                      e.currentTarget.src = PageNotFoundImage;
                    }}
                  />
                  <GrammarOverlay
                    evaluation={computed.currentGrammarEvaluation}
                    pageIndex={pageIdx}
                    selectedErrorId={selectedGrammarErrorId}
                    onSelect={handleSelectGrammarError}
                    editingBox={editingBox}
                    containerRef={{ current: pageContainerRefs.current[pageIdx] ?? null }}
                    onEditingBoxChange={handleEditingBoxChange}
                    onEditingBoxChangeEnd={handleEditingBoxChangeEnd}
                  />
                  {props.onAddGrammarError && (
                    <button
                      className="absolute top-2 right-2 z-30 flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-white/90 dark:bg-slate-700/90 border border-slate-300 dark:border-slate-600 shadow text-slate-700 dark:text-slate-200 hover:bg-red-50 dark:hover:bg-red-900/30 hover:border-red-400 transition-colors"
                      onClick={e => {
                        e.stopPropagation();
                        void handleAddMistake(pageIdx);
                      }}
                    >
                      <Plus size={12} className="mr-0.5" />
                      {t('Canvas.grading.addMistake')}
                    </button>
                  )}
                </div>
              ))
            ) : (
            <div
              ref={refs.imageContainerRef}
              className="relative shadow-xl transition-transform duration-200 ease-out origin-top bg-white dark:bg-slate-800 rounded-xl"
              style={{ transform: `scale(${state.zoom})`, width: '800px' }}
            >
              <img
                src={computed.pages[state.currentPage]}
                alt={t('Canvas.grading.examPageX', { page: state.currentPage + 1 })}
                className="w-full h-auto block pointer-events-none select-none"
                onError={e => {
                  e.currentTarget.src = PageNotFoundImage;
                }}
              />
              {state.localTaskBoxes
                .filter(box => box.pageNumber === state.currentPage)
                .map(box => {
                  const boxIndex = state.localTaskBoxes.findIndex(existingBox => existingBox.id === box.id);
                  const indexedBoxId = `box-${boxIndex}`;

                  return (
                    <InteractiveBox
                      key={box.id}
                      box={box}
                      isSelected={state.selectedBoxId === box.id}
                      isApproved={computed.mode !== 'answers' && state.localIsApproved}
                      containerRef={refs.imageContainerRef}
                      onSelect={() => actions.setSelectedBoxId(box.id)}
                      onChange={newBox => {
                        actions.setLocalTaskBoxes(prev =>
                          prev.map(existingBox => (existingBox.id === box.id ? newBox : existingBox)),
                        );
                      }}
                      onChangeEnd={async newBox => {
                        if (boxIndex < 0) return;
                        await actions.saveUpdate(indexedBoxId, { boxCoordinates: newBox });
                      }}
                      onDelete={async () => {
                        if (boxIndex < 0) return;
                        actions.setLocalTaskBoxes(prev => prev.filter(existingBox => existingBox.id !== box.id));
                        if (state.selectedBoxId === box.id) {
                          actions.setSelectedBoxId(null);
                          actions.setIsEditingText(false);
                        }
                        await actions.saveUpdate(indexedBoxId, { deleteBoundingBox: true });
                      }}
                      onToggleLock={async () => {
                        if (boxIndex < 0) return;
                        const nextApproved = box.approved !== true;
                        actions.setLocalTaskBoxes(prev =>
                          prev.map(existingBox =>
                            existingBox.id === box.id ? { ...existingBox, approved: nextApproved } : existingBox,
                          ),
                        );
                        await actions.saveUpdate(indexedBoxId, { boxApproved: nextApproved });
                      }}
                      onRetry={async () => {
                        if (boxIndex < 0) return;
                        await actions.saveUpdate(indexedBoxId, {
                          boxCoordinates: {
                            x: box.x,
                            y: box.y,
                            width: box.width,
                            height: box.height,
                            pageNumber: box.pageNumber,
                          },
                          rerunOcr: true,
                        });
                      }}
                    />
                  );
                })}
            </div>)}
          </div>

          <div
            onPointerDown={handleResizeStart}
            className={cn(
              'group flex items-center justify-center cursor-col-resize transition-all duration-150 relative z-30',

              'w-1 hover:w-1 rounded-full',
              isResizing ? 'bg-blue-600' : 'bg-slate-300 dark:bg-slate-600 group-hover:bg-blue-400',
            )}
            style={{ userSelect: 'none' }}
          >
            <div
              className={cn(
                'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-150',
                'flex flex-col gap-1 p-1 rounded-full cursor-col-resize',
                isResizing
                  ? 'opacity-100 bg-blue-600'
                  : 'opacity-0 group-hover:opacity-100 bg-white dark:bg-slate-700 shadow-md border border-slate-200 dark:border-slate-600',
              )}
            >
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className={cn('w-1 h-1 rounded-full', isResizing ? 'bg-white' : 'bg-slate-400 dark:bg-slate-500')}
                />
              ))}
            </div>
            <div className="absolute inset-y-0 -left-1 -right-1 cursor-col-resize z-10" />
          </div>

          {/* Right Panel - Details */}
          <div
            className="overflow-auto p-4 flex flex-col gap-4 custom-scrollbar h-full bg-slate-50 dark:bg-secondary rounded-r-xl"
            style={{ width: `${100 - leftPanelWidth}%` }}
          >
            {renderDetailPanels()}
          </div>
        </div>
      )}
    </div>
  );
}
